export const SPREADSHEET_TYPE = {
    individual: 1,
    pedigree: 2
};

export const OPERATOR_MAPPING = {
      'EQUALS': 1,
      'IS_LESS_THAN': 2,
      'IS_GREATER_THAN': 3,
      'DOES_NOT_EQUAL': 4,
      'IS_LESS_THAN_OR_EQUAL_TO': 5,
      'IS_GREATER_THAN_OR_EQUAL_TO': 6,
      'IS_BLANK': 7,
      'IS_NOT_BLANK': 8,
      'BEGINS_WITH': 9,
      'DOES_NOT_BEGIN_WITH': 10,
      'ENDS_WITH': 11,
      'DOES_NOT_END_WITH': 12,
      'CONTAINS': 13,
      'DOES_NOT_CONTAIN': 14,
      'IN': 15,
      'IS_NOT_IN': 16,
      'LIKE': 17,
      'IS_NOT_LIKE': 18,
      'IS_NULL': 19,
      'IS_NOT_NULL': 20,
      // Aggregate operators
      'AVG': 100,
      'COUNT': 101,
      'MAX': 102,
      'MIN': 103,
      'SUM': 104,
      'CONCAT': 105
};
    
export const SPREADSHEET_DEFAULTS = {
  WIDTH: 100,
};

export const SPREADSHEET_FIELD_CONTEXT = {
  INDIVIDUAL: "IDS_CONTEXT",
  PEDIGREE: "PDS_CONTEXT",
};

/**
 * Resolves field context from a field object to the API string (IDS_CONTEXT or PDS_CONTEXT).
 * Handles context as number (1=individual, 2=pedigree) or fieldcontext as string.
 * @param {object} field - Field with context or fieldcontext
 * @param {string} defaultVal - Fallback when field context cannot be resolved
 * @returns {string} "IDS_CONTEXT" or "PDS_CONTEXT"
 */
export const resolveFieldContextForApi = (field, defaultVal) => {
  if (!field) return defaultVal;
  const ctx = field.fieldcontext ?? field.context;
  if (ctx === undefined || ctx === null) return defaultVal;
  if (ctx === 2 || ctx === "PDS_CONTEXT" || ctx === "pedigree") return SPREADSHEET_FIELD_CONTEXT.PEDIGREE;
  if (ctx === 1 || ctx === "IDS_CONTEXT" || ctx === "individual") return SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL;
  return defaultVal;
};

/**
 * For individual spreadsheets, pedigree fields may be incorrectly stored as IDS_CONTEXT.
 * Use field name heuristics to correct when the name suggests pedigree (e.g. "Maternal AJ for Pedigree").
 * @param {object} field - Field with fieldname, fieldcontext
 * @param {string} resolvedContext - Already resolved context from resolveFieldContextForApi
 * @param {boolean} isIndividualSpreadsheet - True when spreadsheet type is individual
 * @returns {string} "IDS_CONTEXT" or "PDS_CONTEXT"
 */
export const correctFieldContextForPedigreeByName = (field, resolvedContext, isIndividualSpreadsheet) => {
  if (!isIndividualSpreadsheet || resolvedContext === SPREADSHEET_FIELD_CONTEXT.PEDIGREE) return resolvedContext;
  const name = (field?.fieldname ?? field?.name ?? "").toLowerCase();
  if (name.includes("for pedigree") || name.includes(" pedigree") || name.endsWith(" pedigree")) {
    return SPREADSHEET_FIELD_CONTEXT.PEDIGREE;
  }
  return resolvedContext;
};

/**
 * Gets default field context from folder when field context is missing.
 * Use when building payload from management view where column/condition has folder.
 * @param {object} folder - Folder with context (1=individual, 2=pedigree)
 * @param {string} spreadsheetDefault - Default from spreadsheet type when folder has no context
 * @returns {string} "IDS_CONTEXT" or "PDS_CONTEXT"
 */
export const getDefaultFieldContextFromFolder = (folder, spreadsheetDefault) => {
  if (!folder || folder.context === undefined || folder.context === null) return spreadsheetDefault;
  if (folder.context === 2) return SPREADSHEET_FIELD_CONTEXT.PEDIGREE;
  if (folder.context === 1) return SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL;
  return spreadsheetDefault;
};
